"use client";

import { Building2, LogOut, Moon, Sun, User } from "lucide-react";

import { AccountRailAvatar } from "@/components/account-rail-avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";

import { logout } from "@/app/actions";
import { useTheme } from "@/providers/theme";
import { useRouter } from "next/navigation";
import { startNavigationFeedback } from "@/lib/navigation-feedback";

export function NavUser({
  placement = "sidebar",
}: {
  placement?: "sidebar" | "header";
}) {
  const { isMobile, setOpenMobile } = useSidebar();
  const { theme, toggleTheme } = useTheme();
  const router = useRouter();

  return (
    <SidebarMenu
      className={
        placement === "header" ? "w-auto items-center" : "items-center"
      }
    >
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              aria-label="Account menu"
              className={
                placement === "header"
                  ? "group/account flex !size-9 shrink-0 items-center !justify-center !gap-0 rounded-lg !bg-transparent !p-1 focus-visible:!outline-none"
                  : "flex max-h-8 !w-8 shrink-0 items-center !justify-center !gap-0 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground md:p-0"
              }
            >
              <AccountRailAvatar
                kind="user"
                header={placement === "header"}
                fallback={null}
              />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={0}
            collisionPadding={8}
          >
            <DropdownMenuGroup>
              <DropdownMenuItem
                onClick={() => {
                  if (isMobile) setOpenMobile(false);
                  startNavigationFeedback("/settings/profile");
                  router.push("/settings/profile");
                }}
              >
                <User />
                My Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={toggleTheme}>
                {theme === "dark" ? (
                  <>
                    <Sun />
                    Light Mode
                  </>
                ) : (
                  <>
                    <Moon />
                    Dark Mode
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  if (isMobile) setOpenMobile(false);
                  startNavigationFeedback("/settings/organizations");
                  router.push("/settings/organizations");
                }}
              >
                <Building2 />
                My Organizations
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-red-500 focus:text-red-500"
              onClick={async (e) => {
                e.preventDefault();
                try {
                  await logout();
                } finally {
                  window.dispatchEvent(new Event("portal-session-changed"));
                }
              }}
            >
              <LogOut />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
